/**
 * Signature avancée par clé d'accès (décision de Franck du 15/10/2026,
 * parcours A) - remplace l'étape du code.
 *
 * Lot 2 (Franck, 15/10/2026) : l'INSCRIPTION passe avant le document
 * (InscriptionCle, affichée par App), dans cet ordre :
 * 1. Le compte : créé avec un mot de passe s'il n'existe pas, sinon le mot de
 *    passe du compte existant est demandé - qui tient un lien transféré ne
 *    rattache pas une clé au compte d'un autre.
 * 2. Vérification d'identité certifiée (PVID) chez notre prestataire, dans un
 *    nouvel onglet, avec les prénoms et nom écrits comme sur la pièce.
 * 3. Quand le serveur a lu le verdict DÉFINITIF, le signataire crée sa clé.
 * Ensuite seulement : lecture, consentement, visuel, puis « Je signe avec ma
 * clé » (CleAcces, à la place du code).
 *
 * Lot 2bis (15/10/2026) : nom vérifié différent du nom indiqué par
 * l'émetteur - selon son réglage, le signataire signe sous son nom vérifié
 * (autoriser), le demande à l'émetteur (demander), ou doit le contacter
 * (interdire). Refait au skill impeccable : une carte étroite, les étapes,
 * des champs pleine largeur, l'action principale sous son formulaire,
 * transmettre et refuser en pied, discrets.
 *
 * Textes en français seulement : parcours en essai, caché, tant que l'avocat
 * n'a pas répondu ; la traduction suivra son ouverture.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, corrigerNom, creerCle, demarrerInscription, etatCle, fetchView, prouverCompte, signerAvecCle,
  type EtatCle } from "../api";
import { ActionsDiscretes, Ico } from "../components/Icone";
import { SurTelephone, surOrdinateur } from "../components/SurTelephone";

function message(e: unknown): string {
  if (e instanceof ApiError) return e.detail ?? "Une erreur est survenue. Réessayez dans un instant.";
  if (e instanceof DOMException && e.name === "NotAllowedError") {
    return "La clé n'a pas été utilisée (opération annulée ou délai dépassé). Réessayez.";
  }
  return "Votre appareil n'a pas pu utiliser la clé. Réessayez, ou essayez depuis votre téléphone.";
}

function useEtatCle(token: string) {
  const [etat, setEtat] = useState<EtatCle | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const lire = useCallback(async () => {
    try {
      setEtat(await etatCle(token));
    } catch (e) {
      setErreur(message(e));
    }
  }, [token]);
  useEffect(() => {
    void lire();
  }, [lire]);
  return { etat, lire, erreur, setErreur };
}

function Chargement({ erreur }: { erreur: string | null }) {
  return (
    <section className="card stack carte-etroite">
      <div className="spinner" role="status" aria-label="Chargement" />
      {erreur && <p className="alert alert--error" role="alert">{erreur}</p>}
    </section>
  );
}

interface SignatureProps {
  transmettre: string;
  token: string;
  onSigned: () => void;
  onDecline: () => void;
  refuser: string;
}

/** Dernière étape : la clé signe (l'inscription est faite avant le document). */
export function CleAcces({ token, transmettre, onSigned, onDecline, refuser }: SignatureProps) {
  const { etat, lire, erreur, setErreur } = useEtatCle(token);
  const [occupe, setOccupe] = useState(false);
  const [ordinateur] = useState(surOrdinateur);
  const [ici, setIci] = useState(false);
  const qr = ordinateur && !ici && Boolean(etat?.pret);

  // QR code affiché : le signataire signe sur son téléphone ; l'ordinateur le
  // constate et passe à « signé » sans qu'on recharge la page.
  useEffect(() => {
    if (!qr) return;
    const id = window.setInterval(() => {
      fetchView(token).then((v) => {
        if (v.signed || v.signer_status === "authenticated") onSigned();
      }, () => undefined);
    }, 5000);
    return () => window.clearInterval(id);
  }, [qr, token, onSigned]);

  if (!etat) return <Chargement erreur={erreur} />;
  // Filet : un signataire arrivé ici sans clé (onglet resté ouvert) s'inscrit sur place.
  if (!etat.pret) {
    return <InscriptionCle token={token} transmettre={transmettre} onPret={() => void lire()} onRecharger={() => void lire()}
                           onDecline={onDecline} refuser={refuser} />;
  }

  if (qr) {
    return (
      <section className="card stack carte-etroite">
        <div>
          <h1 className="card__title">Continuer sur mon téléphone</h1>
          <p className="card__lead">
            Votre clé est gardée dans votre téléphone : signez dessus, avec Face ID, votre empreinte ou son code.
          </p>
        </div>
        <SurTelephone ici="Signer quand même sur cet ordinateur" onIci={() => setIci(true)} />
        <ActionsDiscretes transmettre={transmettre} onDecline={onDecline} refuser={refuser} />
      </section>
    );
  }

  return (
    <section className="card stack carte-etroite">
      <div>
        <h1 className="card__title">Signez avec votre clé</h1>
        <p className="card__lead">
          {ordinateur
            ? "Votre ordinateur vous demande d'utiliser votre clé (empreinte, code ou clé de sécurité) : c'est votre "
              + "signature."
            : "Votre téléphone vous demande Face ID, votre empreinte ou son code : c'est votre signature."}
        </p>
      </div>
      {erreur && <p className="alert alert--error" role="alert">{erreur}</p>}
      <button type="button" className="btn btn--primary btn--large btn--bloc" disabled={occupe}
              onClick={() => {
                setOccupe(true);
                setErreur(null);
                signerAvecCle(token).then(onSigned, (e) => setErreur(message(e))).finally(() => setOccupe(false));
              }}>
        <Ico nom="cle" /> {occupe ? "Signature en cours…" : "Je signe avec ma clé"}
      </button>
      <ActionsDiscretes transmettre={transmettre} onDecline={onDecline} refuser={refuser} />
    </section>
  );
}

interface InscriptionProps {
  token: string;
  /** Compte et clé sont prêts : le parcours de signature s'ouvre. */
  onPret: () => void;
  /** Lot 2bis : une demande vient de partir à l'émetteur (la vue montre l'attente). */
  onRecharger: () => void;
  onDecline: () => void;
  refuser: string;
  transmettre: string;
  /** Le nom de l'émetteur, tel que le signataire le connaît. */
  emetteur?: string | null;
  /** Lot 2 : si l'émetteur l'autorise, la secrétaire transmet AVANT le PVID. */
  onTransmettre?: () => void;
  /** Lot 2ter : pourquoi la transmission est grisee (sinon null). */
}

const ETAPES = ["Compte", "Identité", "Clé"];

function Etapes({ etape }: { etape: number }) {
  return (
    <ol className="etapes" aria-label={`Étape ${etape} sur 3`}>
      {ETAPES.map((nom, i) => {
        const n = i + 1;
        const fait = n < etape;
        return (
          <li key={nom} className={fait ? "etapes--fait" : undefined} aria-current={n === etape ? "step" : undefined}>
            <span className="etapes__rond">{fait ? <Ico nom="coche" taille={14} /> : n}</span>
            <span className="etapes__nom">{nom}</span>
          </li>
        );
      })}
    </ol>
  );
}

function Champ({ id, libelle, aide, ...input }: { id: string; libelle: string; aide?: string }
  & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="champ">
      <label className="field-label" htmlFor={id}>{libelle}</label>
      <input id={id} className="saisie" aria-describedby={aide ? `${id}-aide` : undefined} {...input} />
      {aide && <p className="champ__aide" id={`${id}-aide`}>{aide}</p>}
    </div>
  );
}

export function InscriptionCle({ token, transmettre, onPret, onRecharger, onDecline, refuser, emetteur, onTransmettre }:
  InscriptionProps) {
  const { etat, lire, erreur, setErreur } = useEtatCle(token);
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [lien, setLien] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [ordinateur] = useState(surOrdinateur);
  const [ici, setIci] = useState(false);
  const client = emetteur || "l'émetteur";

  // Une seule fois : onPret recharge la vue, qui démonte cet écran.
  const prevenu = useRef(false);
  useEffect(() => {
    if (etat?.inscription?.lien) setLien(etat.inscription.lien);
    if (etat?.pret && !prevenu.current) {
      prevenu.current = true;
      onPret();
    }
  }, [etat, onPret]);

  // Vérification en cours : le verdict arrive après la revue humaine.
  const enCours = etat?.inscription?.statut === "pvid";
  // 20/10/2026 : sur ordinateur, identité et clé se font sur le téléphone ;
  // le compte, lui, peut se créer ici.
  const compteFait = Boolean(etat?.compte_prouve) || (Boolean(etat?.inscrit) && Boolean(etat?.compte_existant));
  const nomDifferent = compteFait && Boolean(etat?.nom_verifie)
    && (Boolean(etat?.cle_autre_nom) || etat?.inscription?.motif === "nom_different");
  const qr = ordinateur && !ici && compteFait && !nomDifferent && Boolean(etat?.disponible || etat?.inscrit);
  // Le QR code affiché, l'ordinateur suit aussi ce qui se fait sur le téléphone.
  useEffect(() => {
    if (!enCours && !qr) return;
    const id = window.setInterval(() => void lire(), 5000);
    return () => window.clearInterval(id);
  }, [enCours, qr, lire]);

  const agir = async (action: () => Promise<void>) => {
    setOccupe(true);
    setErreur(null);
    try {
      await action();
    } catch (e) {
      setErreur(message(e));
    } finally {
      setOccupe(false);
    }
  };

  if (!etat) return <Chargement erreur={erreur} />;

  const statut = etat.inscription?.statut;
  // compteFait : compte prouvé sur ce lien, ou clé déjà là (il ne manque alors que le compte).
  const creation = !etat.compte_existant;
  const etape = !compteFait ? 1 : etat.inscrit ? 3 : statut === "verifiee" ? 3 : 2;
  const refusee = etat.demande?.type === "nom" && etat.demande.statut === "refusee";
  const mdpCourt = creation && motDePasse.length > 0 && motDePasse.length < 12;
  const mdpDifferent = creation && confirmation.length > 0 && confirmation !== motDePasse;

  let corps: React.ReactNode;
  if (!compteFait) {
    corps = (
      <form className="formulaire" noValidate onSubmit={(e) => {
        e.preventDefault();
        if (creation && (motDePasse.length < 12 || motDePasse !== confirmation)) {
          setErreur(motDePasse.length < 12 ? "Le mot de passe doit faire au moins 12 caractères."
            : "Les deux mots de passe ne sont pas identiques.");
          return;
        }
        if (!motDePasse) {
          setErreur("Saisissez le mot de passe de votre compte.");
          return;
        }
        void agir(async () => { await prouverCompte(token, motDePasse); setMotDePasse(""); await lire(); });
      }}>
        {creation ? (
          <>
            <p className="champ__aide" style={{ fontSize: "1rem" }}>
              Choisissez le mot de passe de votre compte Sign365. Votre adresse est celle qui a reçu ce lien.
            </p>
            <Champ id="cle-mdp" libelle="Mot de passe" type="password" autoComplete="new-password" value={motDePasse}
                   aide="12 caractères au moins." aria-invalid={mdpCourt || undefined}
                   onChange={(e) => setMotDePasse(e.target.value)} />
            <Champ id="cle-mdp2" libelle="Confirmez le mot de passe" type="password" autoComplete="new-password"
                   value={confirmation} aria-invalid={mdpDifferent || undefined}
                   onChange={(e) => setConfirmation(e.target.value)} />
          </>
        ) : (
          <>
            <p className="champ__aide" style={{ fontSize: "1rem" }}>
              Un compte Sign365 existe déjà pour votre adresse. Saisissez son mot de passe pour y rattacher votre clé.
            </p>
            <Champ id="cle-mdp" libelle="Mot de passe du compte" type="password" autoComplete="current-password"
                   value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} />
          </>
        )}
        {erreur && <p className="alert alert--error" role="alert">{erreur}</p>}
        <button type="submit" className="btn btn--primary btn--bloc" disabled={occupe}>
          {occupe ? "Vérification…" : creation ? "Créer mon compte" : "Continuer"}
        </button>
      </form>
    );
  } else if (qr) {
    corps = (
      <div className="formulaire">
        <p className={statut === "verifiee" ? "alert alert--success" : "alert alert--info"} role="status">
          {statut === "verifiee"
            ? <><Ico nom="coche" /> Votre identité est vérifiée. Dernière étape : créer votre clé, sur votre téléphone.</>
            : "Votre compte est prêt. La suite se fait sur votre téléphone : la vérification de votre identité, "
              + "puis la création de votre clé, qui restera dans le téléphone."}
        </p>
        <SurTelephone ici="Continuer quand même sur cet ordinateur" onIci={() => setIci(true)} />
      </div>
    );
  } else if (nomDifferent) {
    const reglage = etat.correction_nom ?? "demander";
    corps = (
      <div className="formulaire">
        <p className={refusee ? "alert alert--error" : "alert alert--info"} role="status">
          {refusee
            ? `${emetteur || "L'émetteur"} a refusé la correction du nom. Contactez-le pour qu'il corrige le nom indiqué, `
              + "ou qu'il envoie le document à la bonne personne."
            : "Votre identité est vérifiée, mais elle ne correspond pas au nom indiqué pour ce document."}
        </p>
        <dl className="noms">
          <div><dt>Nom indiqué par {client}</dt><dd>{etat.nom_attendu || "—"}</dd></div>
          <div><dt>Votre nom vérifié</dt><dd>{etat.nom_verifie}</dd></div>
        </dl>
        {erreur && <p className="alert alert--error" role="alert">{erreur}</p>}
        {refusee ? null : reglage === "interdire" ? (
          <p className="champ__aide" style={{ fontSize: "1rem" }}>
            Contactez {client} pour qu'il corrige le nom indiqué, ou qu'il envoie le document à la bonne personne.
          </p>
        ) : (
          <>
            <p className="champ__aide" style={{ fontSize: "1rem" }}>
              {reglage === "autoriser"
                ? "Le document portera votre nom vérifié. Vous n'aurez pas à refaire la vérification d'identité."
                : `${emetteur || "L'émetteur"} doit valider ce changement avant que vous signiez. Vous serez prévenu par e-mail.`}
            </p>
            <button type="button" className="btn btn--primary btn--bloc" disabled={occupe}
                    onClick={() => void agir(async () => {
                      const r = await corrigerNom(token);
                      if (r === "demandee") onRecharger();
                      else await lire();
                    })}>
              {occupe ? "Envoi…" : reglage === "autoriser" ? `Signer sous le nom ${etat.nom_verifie}`
                : `Demander la correction à ${client}`}
            </button>
          </>
        )}
      </div>
    );
  } else if (!etat.disponible && !etat.inscrit) {
    corps = <p className="alert alert--error" role="alert">
      La vérification d'identité est momentanément indisponible. Réessayez plus tard.</p>;
  } else if (statut === "verifiee") {
    corps = (
      <div className="formulaire">
        <p className="alert alert--success" role="status">
          <Ico nom="coche" /> Votre identité est vérifiée. Dernière étape : votre clé.</p>
        <p className="champ__aide" style={{ fontSize: "1rem" }}>
          {ordinateur
            ? "Votre ordinateur va vous demander de créer la clé (empreinte, code ou clé de sécurité). Elle restera "
              + "sur cet ordinateur."
            : "Votre téléphone va vous demander Face ID, votre empreinte ou son code. La clé reste dans votre téléphone."}
        </p>
        {erreur && <p className="alert alert--error" role="alert">{erreur}</p>}
        <button type="button" className="btn btn--primary btn--bloc" disabled={occupe}
                onClick={() => void agir(async () => { await creerCle(token); await lire(); })}>
          <Ico nom="cle" /> {occupe ? "Création…" : "Créer ma clé"}
        </button>
      </div>
    );
  } else if (statut === "pvid") {
    corps = (
      <div className="formulaire">
        <p className="alert alert--info" role="status">
          Vérification d'identité en cours. Cette page se met à jour seule quand elle est terminée (la validation peut
          prendre une minute).
        </p>
        {lien && (
          <a className="btn btn--secondary btn--bloc" href={lien} target="_blank" rel="noopener noreferrer">
            Ouvrir la vérification d'identité <Ico nom="droite" />
          </a>
        )}
      </div>
    );
  } else {
    corps = (
      <form className="formulaire" noValidate onSubmit={(e) => {
        e.preventDefault();
        if (!prenom.trim() || !nom.trim()) {
          setErreur("Saisissez vos prénoms et votre nom de naissance.");
          return;
        }
        void agir(async () => { setLien(await demarrerInscription(token, prenom.trim(), nom.trim())); await lire(); });
      }}>
        {statut === "echouee" && (
          <p className="alert alert--error" role="alert">
            La vérification précédente n'a pas abouti. Vérifiez vos prénoms et nom, tels qu'écrits sur votre pièce
            d'identité, puis recommencez.
          </p>
        )}
        <p className="champ__aide" style={{ fontSize: "1rem" }}>
          Écrivez vos prénoms et votre nom de naissance <strong>exactement comme sur votre pièce
          d'identité</strong> : la vérification les compare.
        </p>
        <Champ id="cle-prenom" libelle="Prénom(s)" value={prenom} autoComplete="given-name" autoCapitalize="words"
               onChange={(e) => setPrenom(e.target.value)} />
        <Champ id="cle-nom" libelle="Nom de naissance" value={nom} autoComplete="family-name" autoCapitalize="characters"
               onChange={(e) => setNom(e.target.value)} />
        {erreur && <p className="alert alert--error" role="alert">{erreur}</p>}
        <button type="submit" className="btn btn--primary btn--bloc" disabled={occupe || !etat.disponible}>
          {occupe ? "Préparation…" : "Vérifier mon identité"}
        </button>
      </form>
    );
  }

  return (
    <section className="card stack carte-etroite">
      <div>
        <h1 className="card__title">Avant de signer : votre compte et votre clé</h1>
        <p className="card__lead">
          Ce document se signe avec une clé gardée dans votre téléphone, que vous utilisez avec Face ID, votre
          empreinte ou son code. Vous la créez une seule fois, après une vérification de votre identité ; vous lirez
          ensuite le document.
        </p>
        {ordinateur && !compteFait && (
          <p className="note-telephone">
            <Ico nom="telephone" /> Vous êtes sur un ordinateur : créez votre compte ici, puis un QR code vous
            permettra de continuer sur votre téléphone.
          </p>
        )}
      </div>
      <Etapes etape={etape} />
      {corps}
      <ActionsDiscretes onTransmettre={onTransmettre} transmettre={transmettre} onDecline={onDecline} refuser={refuser} />
    </section>
  );
}
